// components/recurringSummary.jsx
import React from 'react';
import PropTypes from 'prop-types';
import ShowCurrencies from '../../utils/showCurrencies';
import CustomButton from '../general/button';
import { useNavigate } from 'react-router-dom';
import { setDailyDonationStatus } from '../../utils/setDailyDonationStatus';

const RecurringSummary = ({
  selectedProducts,
  donationSchedule,
  timeZone,
  recurrence,
  dayValue,
  monthValue,         // e.g. '4'
  totalAmount,
  currency,
  viewCalendar,
  selectedConditions,
  donationOption,
  monthsOfYear,       // array of {value, label}, e.g. 12 items for Jan->Dec
}) => {
  const navigate = useNavigate();

  // 1) Minimum donation logic
  const MINIMUM_DONATION = currency.toLowerCase() === 'myr' ? 200 : 50;
  const isMinimumMet = totalAmount >= MINIMUM_DONATION;

  // 2) If no products, total is 0; else compute from selectedProducts
  const _totalAmount = selectedProducts.length > 0
    ? selectedProducts.reduce(
        (acc, product) => acc + (product.quantity * (product[currency.toLowerCase()] || 0)),
        0
      )
    : 0;

  // 3) Build the recurrence text
  //    - parse `monthValue`
  //    - safely index into `monthsOfYear`
  let recurrenceText = '';

  // Fall back to an empty array if monthsOfYear is missing or not an array
  const safeMonths = Array.isArray(monthsOfYear) ? monthsOfYear : [];

  const parsedMonth = parseInt(monthValue, 10); // e.g. '4' => 4
  let monthDisplay = '...';
  // Ensure it's 1..12 and we actually have that many entries in safeMonths
  if (!isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= safeMonths.length) {
    // e.g. safeMonths[3] => "April"
    monthDisplay = safeMonths[parsedMonth - 1].label;
  }

  if (recurrence === 'daily') {
    recurrenceText = 'Daily donation';
  } else if (recurrence === 'weekly') {
    // Capitalize dayValue
    const dayLabel = dayValue
      ? dayValue.charAt(0).toUpperCase() + dayValue.slice(1).toLowerCase()
      : '...';
    recurrenceText = `Weekly donation, every ${dayLabel}`;
  } else if (recurrence === 'monthly') {
    recurrenceText = `Monthly donation, on day ${dayValue || '...'}`;
  } else if (recurrence === 'yearly') {
    recurrenceText = `Yearly donation on ${monthDisplay} day ${dayValue || '...'}`;
  }

  // 4) Decide if Donate Now button is disabled
  let isBtnDisable = false;
  if (!isMinimumMet) {
    isBtnDisable = true;
  } else if (donationOption === 'custom' && (!selectedConditions || !selectedConditions.length)) {
    isBtnDisable = true;
  }

  return (
    <div className="w-full bg-[#14A2DC] text-white rounded-md p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-semibold mb-4 pb-4 border-b border-white">
        Donation Summary
      </h1>

      {/* 1. Selected Appeals */}
      <div className="mb-6 pb-4 border-b border-white">
        <h2 className="text-xl font-semibold mb-2">Your selected appeals:</h2>
        {selectedProducts.length > 0 ? (
          <ul className="list-none">
            {selectedProducts.map((product) => {
              const lineAmount = (product[currency.toLowerCase()] || 0) * product.quantity;
              return (
                <li key={product.id} className="flex justify-between items-center">
                  <span className="font-thin text-sm">{product.name}</span>
                  <span className="font-bold text-lg">
                    {ShowCurrencies(currency, lineAmount)}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No appeals selected.</p>
        )}
      </div>

      {/* 2. Recurring Pattern */}
      <div className="mb-6 pb-4 border-b border-white">
        <h2 className="text-lg font-semibold mb-2">Recurring Pattern:</h2>
        {recurrenceText ? <p>{recurrenceText}</p> : <p>No recurrence selected.</p>}
      </div>

      {/* 3. Total Amount */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Total Amount:</h2>
        <div className="text-2xl font-bold">
          {ShowCurrencies(currency, _totalAmount)}
        </div>
      </div>

      <div className="mb-3 pb-2 border-b border-white">
        <h3 className="mb-2">
          Note: You will <strong className="mx-0.5">not pay anything</strong> now
        </h3>
      </div>
      <div className="mb-3 pb-2">
        <h3 className="cursor-pointer mb-2 underline" onClick={viewCalendar}>
          See your donation schedule here
        </h3>
      </div>

      {/* 4. Minimum Donation Warning */}
      {!isMinimumMet ? (
        <div className="mb-4 p-2 bg-red-500 text-white rounded-md">
          <p className="text-sm">
            The minimum total donation amount is{' '}
            {ShowCurrencies(currency, MINIMUM_DONATION)}. Please adjust your selections to
            meet this requirement.
          </p>
        </div>
      ) : null}

      {/* 5. Donate Now Button */}
      <CustomButton
        title="Donate Now"
        className={`mt-4 w-full hover:!bg-[#f60362] ${
          isBtnDisable ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => {
          if (!isBtnDisable) {
            setDailyDonationStatus();
            // Possibly store the 'recurrence' or do your logic
            // e.g. localStorage.setItem('RECURRING_DONATION_TYPE', JSON.stringify(recurrence));
            navigate('/checkout', { state: { from: 'recurring-giving' } });
          }
        }}
        disabled={isBtnDisable}
      />
    </div>
  );
};

RecurringSummary.propTypes = {
  selectedProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
    })
  ).isRequired,
  donationSchedule: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    })
  ),
  timeZone: PropTypes.string,
  recurrence: PropTypes.string,
  dayValue: PropTypes.string,
  monthValue: PropTypes.string,
  totalAmount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  viewCalendar: PropTypes.func.isRequired,
  selectedConditions: PropTypes.array,
  donationOption: PropTypes.string,
  monthsOfYear: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

RecurringSummary.defaultProps = {
  monthsOfYear: [], // fallback if not passed
};

export default RecurringSummary;
